import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import Grid from '@material-ui/core/Grid';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Link,
  Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import 'moment/locale/pt-br';
import { useHistory, useLocation } from 'react-router';

import api from 'src/services/api';
import useFetch from 'src/hooks/useFetch';
import { useAlert } from 'src/hooks/useAlert';
import { getEnderecoByCep } from 'src/utils/getEnderecoByCEP';

import { CEP_PATTERN, CNPJ_PATTERN } from 'src/consts/patterns';
import { CAMPO_OBRIGATORIO, UFS_OPTIONS } from 'src/consts/common';

import { Input, InputMask, Select } from 'src/components/Form';
import { Container } from 'src/components/Layouts/Container';
import { Centered } from 'src/components/Centered';
import { Title } from 'src/components/Title';

import schema from './schema';
function UsuarioEmpresa({ isCadastroNovo = false }) {
  const history = useHistory();

  const usuario = useSelector((state) => state.user);

  const { state } = useLocation();

  const message = state?.message || null;

  const existsProcessOpenCompany =
    localStorage.getItem('existsProcessOpenCompany') === 'true';

  const { isLoading, data } = useFetch(
    existsProcessOpenCompany === false &&
      isCadastroNovo === false &&
      '/usuario/empresa'
  );
  const { data: tiposEmpresaTributacao } = useFetch(
    existsProcessOpenCompany === false && '/tiposEmpresaTributacao'
  );

  const { alert, openAlert, closeAlert } = useAlert();

  const [formSubmiting, setFormSubmiting] = useState(false);
  const [tributacaoOptions, setTributacaoOptions] = useState([]);
  const [shouldTipoTributacaoDisabled, setShouldTipoTributacaoDisabled] =
    useState(false);
  const [alertMessage, setAlertMessage] = useState(!!message);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    resetField,
    formState: { errors, isDirty },
  } = useForm({
    mode: 'onBlur',
    resolver: yupResolver(schema),
  });
  const watchTipoEmpresa = watch('tipo_empresa');

  // atualiza os dados do usuário quando a requisicao é concluida
  useEffect(() => {
    reset(data);
  }, [data, reset]);

  async function handleCepBlur(cepValue) {
    if (cepValue) {
      const response = await getEnderecoByCep(cepValue);

      Object.keys(response).forEach((key) => {
        resetField(key, {
          defaultValue: response[key],
        });
      });
    }
  }

  async function onSubmit(formData) {
    try {
      setFormSubmiting(true);

      await api.post('/empresas', {
        id_usuario: usuario?.id,
        ...formData,
      });

      if (isCadastroNovo) {
        history.push('/');
      } else {
        openAlert('Dados atualizados com sucesso!');

        reset(formData);
      }
    } catch (err) {
      openAlert(
        `Erro ao ${isCadastroNovo ? 'cadastrar' : 'atualizar'} dados!`,
        'error'
      );
    } finally {
      setFormSubmiting(false);
    }
  }

  useEffect(() => {
    if (tiposEmpresaTributacao?.tipoEmpresa?.length > 0) {
      const currentTipoEmpresa = tiposEmpresaTributacao.tipoEmpresa.find(
        (tipoEmpresa) => tipoEmpresa.id === watchTipoEmpresa
      );

      const meiOptionIndex = tiposEmpresaTributacao.tipoTributacao.findIndex(
        (tipoTributacao) => tipoTributacao.nome === 'MEI'
      );

      if (currentTipoEmpresa) {
        if (currentTipoEmpresa?.nome === 'MEI') {
          setTributacaoOptions([
            tiposEmpresaTributacao.tipoTributacao[meiOptionIndex],
          ]);

          resetField('tipo_tributacao', {
            defaultValue:
              tiposEmpresaTributacao.tipoTributacao[meiOptionIndex].id,
          });

          setShouldTipoTributacaoDisabled(true);
        } else {
          setShouldTipoTributacaoDisabled(false);

          const currentTiposTributacao = [
            ...tiposEmpresaTributacao.tipoTributacao,
          ];

          currentTiposTributacao.splice(meiOptionIndex, 1);

          resetField('tipo_tributacao', {
            defaultValue: currentTiposTributacao[0].id,
          });

          setTributacaoOptions(currentTiposTributacao);
        }
      }
    }
  }, [watchTipoEmpresa, tiposEmpresaTributacao, resetField]);

  return (
    <>
      {usuario?.existsProcessOpenCompany || message ? (
        <Container maxwidth={600}>
          <>
            <Collapse in={alertMessage}>
              <Alert severity="info" onClose={() => setAlertMessage(false)}>
                {message}
              </Alert>
            </Collapse>
            <Title>Olá{usuario?.nome && `, ${usuario?.nome}`}</Title>
            <Typography>
              A sua empresa já está em processo de abertura.
            </Typography>
            <br />
            <Typography>
              Para que possamos dar agilidade ao processo precisamos que você
              preencha o formulário que chegou no seu e-mail ou pelo link
              abaixo.
            </Typography>
            <br />
            <Link
              href="https://docs.google.com/forms/d/e/1FAIpQLSeMPuGsjk_k2Ry3gzog6Gy0UIr0YDzLZIVytPaycToiM3jcWg/viewform"
              target="_blank"
            >
              <Typography>[clique aqui]</Typography>
            </Link>
            <br />
            <Typography>
              Caso você já tenha preenchido por favor desconsidere.
            </Typography>
            <br />
            <Typography>
              Obrigado,
              <br /> Equipe de Assessoria Fiscal
            </Typography>
          </>
        </Container>
      ) : (
        <Container isLoading={isLoading}>
          <Collapse in={alert.open}>
            <Alert severity={alert.severity} onClick={closeAlert}>
              {alert.message}
            </Alert>
          </Collapse>
          <Title>Empresa</Title>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <InputMask
                control={control}
                id="cnpj"
                name="cnpj"
                label="CNPJ"
                variant="outlined"
                pattern={CNPJ_PATTERN}
                error={!!errors?.cnpj}
                helperText={
                  errors?.cnpj ? errors.cnpj.message : CAMPO_OBRIGATORIO
                }
                InputLabelProps={{
                  shrink: true,
                }}
                placeholder="CNPJ"
              />
            </Grid>
            <Grid item xs={12}>
              <Input
                control={control}
                fullWidth
                id="razaoSocial"
                name="razao_social"
                label="Razão Social"
                placeholder="Razão Social"
                error={!!errors?.razao_social}
                helperText={
                  errors?.razao_social
                    ? errors.razao_social.message
                    : CAMPO_OBRIGATORIO
                }
              />
            </Grid>
            <Grid item xs={watchTipoEmpresa ? 3 : 6}>
              <Select
                control={control}
                id="tipoEmpresa"
                name="tipo_empresa"
                label="Tipo da Empresa"
                placeholder="Tipo Empresa"
                options={tiposEmpresaTributacao?.tipoEmpresa}
                error={!!errors?.tipoEmpresa}
                helperText={
                  errors?.tipoEmpresa
                    ? errors.tipoEmpresa.message
                    : CAMPO_OBRIGATORIO
                }
                fullWidth
                disabled={data !== null && data?.tipo_empresa !== null}
              />
            </Grid>
            {watchTipoEmpresa && (
              <Grid item xs={3}>
                <Select
                  control={control}
                  id="tipoTributacao"
                  name="tipo_tributacao"
                  label="Tipo de Tributação"
                  placeholder="Tipo Tributação"
                  options={tributacaoOptions}
                  error={!!errors?.tipoTributacao}
                  helperText={
                    errors?.tipoTributacao
                      ? errors.tipoTributacao.message
                      : CAMPO_OBRIGATORIO
                  }
                  fullWidth
                  disabled={
                    shouldTipoTributacaoDisabled ||
                    (data !== null && data?.tipo_tributacao !== null)
                  }
                />
              </Grid>
            )}

            <Grid item xs={3}>
              <Input
                type="number"
                control={control}
                id="cnae"
                name="cnae"
                label="CNAE"
                placeholder="CNAE"
                error={!!errors?.cnae}
                helperText={errors?.cnae ? errors.cnae.message : ''}
                fullWidth
              />
            </Grid>
            <Grid item xs={3}>
              <Input
                type="number"
                control={control}
                id="codigo_simples"
                name="codigo_simples"
                label="Código Simples"
                placeholder="Código Simples"
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <Input
                type="number"
                control={control}
                id="Inscrição Municipal"
                name="inscricao_municipal"
                label="Inscrição Municipal"
                placeholder="Inscrição Municipal"
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <Input
                control={control}
                id="Login Prefeitura"
                name="login_insc_municipal"
                label="Login Prefeitura"
                placeholder="Login Prefeitura"
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <Input
                control={control}
                id="Senha Prefeitura"
                name="senha_insc_municipal"
                label="Senha Prefeitura"
                placeholder="Senha Prefeitura"
                fullWidth
              />
            </Grid>
          </Grid>
          <Title marginTop={20} marginBottom={10}>
            Endereço
          </Title>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <InputMask
                control={control}
                id="cep"
                name="cep"
                label="CEP"
                variant="outlined"
                pattern={CEP_PATTERN}
                error={!!errors?.cep}
                helperText={
                  errors?.cep ? errors.cep.message : CAMPO_OBRIGATORIO
                }
                onBlur={(event) =>
                  handleCepBlur(event?.nativeEvent?.target?.value)
                }
              />
            </Grid>
            <Input control={control} id="ibge" name="ibge" hidden />
            <Grid item xs={12}>
              <Input
                control={control}
                id="Logradouro"
                name="logradouro"
                label="Logradouro"
                placeholder="Logradouro"
                fullWidth
              />
            </Grid>
            <Grid item xs={3}>
              <Input
                control={control}
                id="numero"
                name="numero"
                label="Número"
                placeholder="Número"
                fullWidth
              />
            </Grid>
            <Grid item xs={5}>
              <Input
                control={control}
                id="complemento"
                name="complemento"
                label="Complemento"
                placeholder="Complemento"
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <Input
                control={control}
                id="bairro"
                name="bairro"
                label="Bairro"
                placeholder="Bairro"
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <Input
                control={control}
                id="cidade"
                name="cidade"
                label="Cidade"
                placeholder="Cidade"
                error={!!errors?.cidade}
                helperText={
                  errors?.cidade ? errors.cidade.message : CAMPO_OBRIGATORIO
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={2}>
              <Select
                control={control}
                id="estado"
                name="estado"
                label="Estado"
                options={UFS_OPTIONS}
                error={!!errors?.estado}
                helperText={errors?.estado ? errors.estado.message : ''}
                fullWidth
              />
            </Grid>
          </Grid>
          <Centered>
            <Box marginTop={4}>
              <Button
                onClick={handleSubmit(onSubmit)}
                size="medium"
                variant="contained"
                color="primary"
                disabled={!isDirty || formSubmiting}
              >
                {formSubmiting ? (
                  <CircularProgress color="primary" size={25} />
                ) : isCadastroNovo ? (
                  'Cadastrar'
                ) : (
                  'Atualizar dados'
                )}
              </Button>
            </Box>
          </Centered>
        </Container>
      )}
    </>
  );
}

UsuarioEmpresa.propTypes = {
  isCadastroNovo: PropTypes.bool,
};

export default UsuarioEmpresa;
